<template>
  <!-- Live sales demo: «what kind of restaurant?» before the three simulated orders load. A card of the
       system's dialog with the platform picker's tiles (two to a row, the emoji above and the name below);
       a tap on a tile is the answer, so the only button is the way out. -->
  <BzDialog card :visible="visible" band="modal"
            :title="$t('message.demoPickerTitle')"
            :subtitle="$t('message.demoPickerHint')"
            mask-testid="demo-category-picker"
            @dismiss="$emit('cancel')">
    <div class="demo-picker__grid">
      <button
        v-for="category in categories"
        :key="category.id"
        type="button"
        class="bz-tile demo-picker__tile"
        :class="{ 'is-selected': category.id === selected }"
        :aria-pressed="category.id === selected"
        :data-testid="`demo-category-${category.id}`"
        @click="$emit('pick', category.id)"
      >
        <span class="demo-picker__emoji" aria-hidden="true" v-html="emojiOf(category)"></span>
        <span class="demo-picker__foot">
          <span class="bz-tile__label">{{ $t(category.labelKey) }}</span>
          <BzIcon v-if="category.id === selected" name="check" class="demo-picker__check" />
        </span>
      </button>
    </div>
    <template #footer>
      <BzButton size="lg" kind="secondary" block :label="$t('message.cancel')"
                data-testid="demo-category-cancel" @click="$emit('cancel')"/>
    </template>
  </BzDialog>
</template>

<script>
import BzButton from '@/ui/components/BzButton.vue';
import BzDialog from '@/ui/components/BzDialog.vue';
import BzIcon from '@/ui/components/BzIcon.vue';
import { categories } from '@/liveDemo/catalog';

const uEmojiParser = require('universal-emoji-parser');

/**
 * The live sales demo's category picker (src/liveDemo/): which kind of restaurant the three simulated orders
 * are. `selected` is the category the demo is running now (the restart row highlights it), or null.
 *
 * @rule   A tap on a tile answers (`pick`, with the category's id); the X-less card's only button, the scrim
 *         and Android's back all leave without choosing (`cancel`). Nothing is stored here: the parent does.
 */
export default {
  name: 'LiveDemoCategoryPicker',
  components: { BzButton, BzDialog, BzIcon },
  props: {
    visible: { type: Boolean, required: true },
    selected: { type: String, default: null },
  },
  emits: ['pick', 'cancel'],
  data() {
    return { categories };
  },
  methods: {
    // The same Twemoji <img> the ticket draws for a product (OrderItem.vue).
    emojiOf(category) {
      return uEmojiParser.parse(category.icon);
    },
  },
};
</script>

<style scoped>
/* The categories: two to a row, each a tile of the system (stacked: the emoji above, the name below),
   at the tile's own height of 96 and 12 apart. */
.demo-picker__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--bz-target-gap);
}

.demo-picker__tile {
  min-width: 0;
  cursor: pointer;
}

/* The emoji at 48, as the platform logos of the manual order. It is a Twemoji <img> with no size of its own. */
.demo-picker__emoji {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--bz-target-min);
  height: var(--bz-target-min);
}

.demo-picker__emoji :deep(img) {
  width: 100%;
  height: 100%;
}

/* The name, and a tick beside it on the one in use: the state is a mark and not only the tile's blue. */
.demo-picker__foot {
  display: flex;
  align-items: center;
  gap: var(--bz-space-2);
  min-width: 0;
}

.demo-picker__check {
  flex: 0 0 auto;
  color: var(--bz-action-ink);
}
</style>
